import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts';
import * as awsApi from '../services/awsApiService';
import {
  CheckCircle, ChevronRight, ChevronLeft, Wifi, Database,
  RefreshCw, Eye, EyeOff, Zap, ToggleLeft, ToggleRight,
  FileText, Users, BarChart3, Shield
} from 'lucide-react';

const CLEARINGHOUSES = [
  { value: 'office_ally',  label: 'Office Ally',              host: 'ftp10.officeally.com' },
  { value: 'renaissance',  label: 'Renaissance / Remote Lite', host: 'sftp.remitdata.com'  },
  { value: 'vyne',         label: 'Vyne Dental',               host: 'sftp.vynedental.com' },
  { value: 'other',        label: 'Other',                     host: ''                    },
];

const DISMISS_KEY  = (id) => `flowrcm_onboarding_dismissed_${id}`;
const COMPLETE_KEY = (id) => `flowrcm_onboarding_complete_${id}`;

export const shouldShowOnboarding = (practice) => {
  if (!practice?.practice_id) return false;
  if (practice.setup_complete) return false;
  if (localStorage.getItem(COMPLETE_KEY(practice.practice_id))) return false;
  if (localStorage.getItem(DISMISS_KEY(practice.practice_id))) return false;
  return true;
};

const Toggle = ({ value, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
    <div>
      <p className="text-sm font-medium text-slate-800">{label}</p>
      {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
    </div>
    <button onClick={() => onChange(!value)} className="flex-shrink-0 ml-4">
      {value
        ? <ToggleRight className="w-8 h-8 text-teal-600" />
        : <ToggleLeft  className="w-8 h-8 text-slate-300" />}
    </button>
  </div>
);

const ResultBanner = ({ result }) => {
  if (!result) return null;
  return (
    <div className={`text-sm px-4 py-3 rounded-lg mb-4 ${result.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
      {result.success ? '✓ ' : '✗ '}{result.message || (result.success ? 'Success' : 'Failed')}
    </div>
  );
};

const isDemoMode = process.env.REACT_APP_MODE === 'demo' || window.location.hostname === 'demo.flowrcmdental.com';

export default function OnboardingWizard({ onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { practice } = useAuth();
  const [step, setStep] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('wizardStep');
    return p !== null ? parseInt(p, 10) : 0;
  });
  const [saving, setSaving] = useState(false);

  // PMS
  const PMS_SYSTEMS = [
    { value: 'waveortho',       label: 'WaveOrtho',       type: 'bot' },
    { value: 'open_dental',     label: 'Open Dental',     type: 'api' },
    { value: 'eaglesoft',       label: 'Eaglesoft',       type: 'bot' },
    { value: 'dentrix_ascend',  label: 'Dentrix Ascend',  type: 'bot' },
    { value: 'carestream',      label: 'Carestream',      type: 'bot' },
  ];
  const [pmsSystem, setPmsSystem]     = useState(isDemoMode ? 'waveortho' : (practice?.pms_system || 'waveortho'));
  const pmsType = PMS_SYSTEMS.find(p => p.value === pmsSystem)?.type || 'bot';
  const [odApiUrl, setOdApiUrl]       = useState('');
  const [odApiKey, setOdApiKey]       = useState('');
  const [pmsUsername, setPmsUsername] = useState(isDemoMode ? 'jessica@brightsmilesdental.com' : '');
  const [pmsPassword, setPmsPassword] = useState(isDemoMode ? 'BrightSmiles2026!' : '');
  const [odTesting, setOdTesting]     = useState(false);
  const [odResult, setOdResult]       = useState(null);
  const [odSaved, setOdSaved]         = useState(isDemoMode || practice?.pms_system != null);

  // Clearinghouse
  const [chProvider, setChProvider]   = useState('office_ally');
  const [chHost, setChHost]           = useState('ftp10.officeally.com');
  const [chPort, setChPort]           = useState('22');
  const [chUsername, setChUsername]   = useState(isDemoMode ? 'brightsmiles_officeally' : '');
  const [chPassword, setChPassword]   = useState(isDemoMode ? 'Sm1l3s@OA2026!' : '');
  const [chUploadDir, setChUploadDir] = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [chTesting, setChTesting]     = useState(false);
  const [chResult, setChResult]       = useState(null);
  const [chSaved, setChSaved]         = useState(isDemoMode || practice?.clearinghouse_configured);

  // Automation
  const [automation, setAutomation] = useState({
    autoAppealEnabled: true,
    agingFlagEnabled:  true,
    remindersEnabled:  true,
    autoScrubEnabled:  true,
    minBalance: 5,
  });

  // Sync wizard step with URL wizardStep param (used by demo tour navigation)
  useEffect(() => {
    const p = new URLSearchParams(location.search).get('wizardStep');
    if (p !== null) setStep(parseInt(p, 10));
  }, [location.search]);

  const STEPS = ['welcome', 'open-dental', 'clearinghouse', 'automation', 'done'];
  const current = STEPS[step];

  const handleDismiss = () => {
    if (practice?.practice_id) localStorage.setItem(DISMISS_KEY(practice.practice_id), '1');
    onClose();
  };

  const handleProviderChange = (val) => {
    setChProvider(val);
    const found = CLEARINGHOUSES.find(c => c.value === val);
    if (found) setChHost(found.host);
    setChResult(null);
  };

  const handleTestOD = async () => {
    if (!odApiUrl || !odApiKey) return;
    setOdTesting(true); setOdResult(null);
    const res = await awsApi.testOpenDentalConnection(odApiUrl, odApiKey);
    setOdResult({ success: res.success, message: res.success ? 'Connection successful' : (res.message || 'Connection failed') });
    setOdTesting(false);
  };

  const handleSavePMS = async () => {
    if (isDemoMode) { setOdSaved(true); setStep(s => s + 1); return; }
    setSaving(true);
    const payload = pmsType === 'api'
      ? { pmsSystem, apiUrl: odApiUrl, apiKey: odApiKey }
      : { pmsSystem, username: pmsUsername, password: pmsPassword };
    const res = await awsApi.saveOpenDentalConfig(pmsSystem, payload);
    setSaving(false);
    if (res.success) { setOdSaved(true); setStep(s => s + 1); }
    else setOdResult({ success: false, message: res.error || 'Save failed' });
  };

  const handleTestCH = async () => {
    if (!chUsername || !chPassword) return;
    setChTesting(true); setChResult(null);
    await awsApi.saveClearinghouseSettings({ clearinghouse: chProvider, host: chHost, port: chPort, username: chUsername, password: chPassword, upload_directory: chUploadDir });
    const res = await awsApi.testClearinghouseConnection();
    setChResult({ success: res.success, message: res.success ? 'Connection successful' : (res.data?.message || res.error || 'Connection failed') });
    setChTesting(false);
  };

  const handleSaveCH = async () => {
    if (isDemoMode) { setChSaved(true); setStep(s => s + 1); return; }
    setSaving(true);
    const res = await awsApi.saveClearinghouseSettings({ clearinghouse: chProvider, host: chHost, port: chPort, username: chUsername, password: chPassword, upload_directory: chUploadDir });
    setSaving(false);
    if (res.success) { setChSaved(true); setStep(s => s + 1); }
    else setChResult({ success: false, message: res.error || 'Save failed' });
  };

  const handleSaveAutomation = async () => {
    if (isDemoMode) { setStep(s => s + 1); return; }
    setSaving(true);
    await awsApi.saveAutomationSettings(automation).catch(() => {});
    setSaving(false);
    setStep(s => s + 1);
  };

  const handleFinish = async () => {
    setSaving(true);
    if (practice?.practice_id) localStorage.setItem(COMPLETE_KEY(practice.practice_id), '1');
    await awsApi.markSetupComplete().catch(() => {});
    setSaving(false);
    onClose();
    navigate('/');
  };

  const progressPct = (step / (STEPS.length - 1)) * 100;

  return (
    <div className="fixed inset-0 z-[9000] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-teal-800 to-teal-600 px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-white">
              <img src="/favicon.svg" alt="FlowRCM" className="w-7 h-7" style={{ borderRadius: 6 }} />
              <span className="font-bold text-lg">FlowRCM Setup</span>
            </div>
            <button
              onClick={handleDismiss}
              className="text-xs text-teal-200 hover:text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors"
            >
              I'll do this later
            </button>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 bg-white/20 rounded-full">
            <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
          {/* Step labels */}
          <div className="flex justify-between mt-2">
            {['Welcome', 'PMS', 'Clearinghouse', 'Automation', 'Done'].map((label, i) => (
              <span key={label} className={`text-[10px] ${i === step ? 'text-white font-semibold' : 'text-teal-300'}`}>{label}</span>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="p-6">

          {/* ── WELCOME ── */}
          {current === 'welcome' && (
            <div className="space-y-5" data-tour="onboarding-welcome">
              <div>
                <div className="text-4xl mb-3">👋</div>
                <h2 className="text-xl font-bold text-slate-900">
                  Welcome to FlowRCM{practice?.name ? `, ${practice.name}` : ''}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Let's connect your systems so the Claim Robot can start working. Takes about 5 minutes.
                </p>
              </div>
              <div className="space-y-2">
                {[
                  { icon: Database, label: 'Connect Your PMS', sub: 'Patients & claims sync every night automatically', done: odSaved },
                  { icon: Wifi,     label: 'Configure Clearinghouse', sub: 'Claims submit as 837D files, ERA payments post automatically', done: chSaved },
                  { icon: Zap,      label: 'Review Automation Settings', sub: 'Auto-appeals, claim scrubbing, payment reminders', done: false },
                ].map(({ icon: Icon, label, sub, done }) => (
                  <div key={label} className={`flex items-start gap-3 p-3 rounded-xl border ${done ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="mt-0.5">{done ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Icon className="w-5 h-5 text-teal-600" />}</div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{label}</p>
                      <p className="text-xs text-slate-500">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button
                data-tour="wizard-get-started"
                onClick={() => setStep(1)}
                className="w-full flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-white py-3 rounded-xl font-semibold text-sm transition-colors"
              >
                Get Started <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* ── PMS ── */}
          {current === 'open-dental' && (
            <div className="space-y-4" data-tour="wizard-pms-step">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Database className="w-5 h-5 text-teal-600" />
                  <h2 className="text-xl font-bold text-slate-900">Connect Your Practice Management System</h2>
                </div>
                <p className="text-sm text-slate-500">FlowRCM syncs patients and claims automatically every night.</p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Practice Management System</label>
                  <select value={pmsSystem} onChange={e => { setPmsSystem(e.target.value); setOdResult(null); }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white">
                    {PMS_SYSTEMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                {pmsType === 'api' ? (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Open Dental API URL</label>
                      <input type="text" placeholder="http://your-server:8080" value={odApiUrl}
                        onChange={e => { setOdApiUrl(e.target.value); setOdResult(null); }}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">API Key</label>
                      <input type="password" placeholder="Your Open Dental API key" value={odApiKey}
                        onChange={e => { setOdApiKey(e.target.value); setOdResult(null); }}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Login Username / Email</label>
                      <input type="text" placeholder="your@email.com" value={pmsUsername}
                        onChange={e => { setPmsUsername(e.target.value); setOdResult(null); }}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1">Password</label>
                      <input type="password" placeholder="Your PMS login password" value={pmsPassword}
                        onChange={e => { setPmsPassword(e.target.value); setOdResult(null); }}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                    </div>
                    <p className="text-xs text-slate-400">FlowRCM uses these credentials to sync your data nightly. Credentials are encrypted and never shared.</p>
                  </>
                )}
              </div>
              <ResultBanner result={odResult} />
              <div className="flex gap-2">
                <button data-tour="wizard-save-pms" onClick={handleSavePMS}
                  disabled={pmsType === 'api' ? (!odApiUrl || !odApiKey || saving) : (!pmsUsername || !pmsPassword || saving)}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-sm font-semibold disabled:opacity-40 transition-colors">
                  {saving ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <>Save & Continue <ChevronRight className="w-3.5 h-3.5" /></>}
                </button>
              </div>
              <div className="flex justify-between">
                <button onClick={() => setStep(0)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={() => setStep(2)} className="text-sm text-slate-400 hover:text-slate-600">
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* ── CLEARINGHOUSE ── */}
          {current === 'clearinghouse' && (
            <div className="space-y-4" data-tour="wizard-clearinghouse-step">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Wifi className="w-5 h-5 text-teal-600" />
                  <h2 className="text-xl font-bold text-slate-900">Configure Clearinghouse</h2>
                </div>
                <p className="text-sm text-slate-500">Enter your SFTP credentials. Claims will submit automatically as 837D EDI files.</p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Clearinghouse</label>
                  <select value={chProvider} onChange={e => handleProviderChange(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white">
                    {CLEARINGHOUSES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">SFTP Host</label>
                    <input type="text" value={chHost} onChange={e => setChHost(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                  </div>
                  <div style={{ width: 72 }}>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Port</label>
                    <input type="text" value={chPort} onChange={e => setChPort(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">SFTP Username</label>
                  <input type="text" value={chUsername} onChange={e => setChUsername(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">SFTP Password</label>
                  <div className="relative">
                    <input type={showPass ? 'text' : 'password'} value={chPassword} onChange={e => setChPassword(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 pr-10 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </div>
              <ResultBanner result={chResult} />
              <div className="flex gap-2">
                <button onClick={handleTestCH} disabled={!chUsername || !chPassword || chTesting}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 border border-teal-600 text-teal-700 rounded-lg text-sm font-semibold hover:bg-teal-50 disabled:opacity-40 transition-colors">
                  {chTesting ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Testing...</> : 'Test Connection'}
                </button>
                <button data-tour="wizard-save-ch" onClick={handleSaveCH} disabled={!chUsername || !chPassword || saving}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-lg text-sm font-semibold disabled:opacity-40 transition-colors">
                  {saving ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <>Save & Continue <ChevronRight className="w-3.5 h-3.5" /></>}
                </button>
              </div>
              <div className="flex justify-between">
                <button onClick={() => setStep(1)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button onClick={() => setStep(3)} className="text-sm text-slate-400 hover:text-slate-600">
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* ── AUTOMATION ── */}
          {current === 'automation' && (
            <div className="space-y-4" data-tour="onboarding-automation">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Automation Settings</h2>
                <p className="text-sm text-slate-500 mt-1">The Claim Robot runs these every 4 hours. Change anytime in Settings.</p>
              </div>
              <div className="border border-slate-200 rounded-xl px-4 divide-y divide-slate-100">
                <Toggle value={automation.autoAppealEnabled} onChange={v => setAutomation(a => ({ ...a, autoAppealEnabled: v }))}
                  label="Auto Appeals" description="Generate appeal letters for denied claims automatically" />
                <Toggle value={automation.agingFlagEnabled} onChange={v => setAutomation(a => ({ ...a, agingFlagEnabled: v }))}
                  label="Aging Alerts" description="Flag claims unpaid after 45 days" />
                <Toggle value={automation.autoScrubEnabled} onChange={v => setAutomation(a => ({ ...a, autoScrubEnabled: v }))}
                  label="Claim Scrubbing" description="AI checks new claims for errors before submission" />
                <Toggle value={automation.remindersEnabled} onChange={v => setAutomation(a => ({ ...a, remindersEnabled: v }))}
                  label="Payment Reminders" description="Email patients with outstanding balances" />
              </div>
              {automation.remindersEnabled && (
                <div className="flex items-center gap-3">
                  <label className="text-sm text-slate-700 whitespace-nowrap">Min balance to remind:</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                    <input type="number" className="border border-slate-300 rounded-lg pl-7 pr-3 py-2 text-sm w-24"
                      value={automation.minBalance} onChange={e => setAutomation(a => ({ ...a, minBalance: parseInt(e.target.value) || 0 }))} />
                  </div>
                </div>
              )}
              <div className="flex justify-between pt-1">
                <button onClick={() => setStep(2)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
                <button data-tour="wizard-save-automation" onClick={handleSaveAutomation} disabled={saving}
                  className="flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-40 transition-colors">
                  {saving ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Saving...</> : <>Save & Continue <ChevronRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}

          {/* ── DONE ── */}
          {current === 'done' && (
            <div className="space-y-5 text-center" data-tour="wizard-done">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-9 h-9 text-teal-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900">You're all set!</h2>
                <p className="text-sm text-slate-500 mt-2">The Claim Robot is ready. It will start working automatically every 4 hours.</p>
              </div>
              <div className="space-y-2 text-left">
                {[
                  { label: PMS_SYSTEMS.find(p => p.value === pmsSystem)?.label || 'PMS', done: odSaved, note: 'Patients & claims sync automatically' },
                  { label: 'Clearinghouse',  done: chSaved, note: 'Claims submit as 837D, ERA posts automatically' },
                  { label: 'Automation',     done: true,    note: 'Scrubbing, appeals, and alerts active' },
                ].map(({ label, done, note }) => (
                  <div key={label} className={`flex items-center gap-3 p-3 rounded-xl border ${done ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                    {done
                      ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      : <div className="w-5 h-5 border-2 border-slate-300 rounded-full flex-shrink-0" />}
                    <div>
                      <p className={`text-sm font-semibold ${done ? 'text-green-800' : 'text-slate-500'}`}>{label} {done ? '— Connected' : '— Not configured'}</p>
                      <p className="text-xs text-slate-500">{done ? note : 'You can configure this in Settings anytime'}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 text-left">
                {[
                  { icon: FileText,  color: 'bg-teal-100 text-teal-700',    title: 'Claims Pipeline',  desc: 'Every claim tracked' },
                  { icon: Shield,    color: 'bg-purple-100 text-purple-700', title: 'Pre-Auth',         desc: 'Authorization tracking' },
                  { icon: Users,     color: 'bg-blue-100 text-blue-700',     title: 'Patients',         desc: 'Balances & collection AI' },
                  { icon: BarChart3, color: 'bg-orange-100 text-orange-700', title: 'Performance',      desc: 'Revenue analytics' },
                ].map(({ icon: Icon, color, title, desc }) => (
                  <div key={title} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-xl">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-800">{title}</p>
                      <p className="text-xs text-slate-500">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={handleFinish} disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-white py-3 rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors">
                {saving ? <><RefreshCw className="w-4 h-4 animate-spin" /> Loading...</> : <><Zap className="w-4 h-4" /> Go to Claim Robot</>}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
